import classNames from 'classnames';
import React from 'react';

import {omit} from 'util/omitKey';

import ScrollableTable, {BaseScrollableTableProps} from './ScrollableTable';

interface ExpandedScrollableTableProps extends Omit<BaseScrollableTableProps, 'calcLeftPadding'> {
  className?: string;
  getRowContentHeight?: (rowIndex?: number) => number;
  isCompact: boolean;
}

// We need to calculate left padding in JS instead of less because FixedDataTable requires widths to be
// passed in. This calculation should be kept in sync with the one used in less/expanded.less
export function calcExpandedLeftPadding(tableWidth: number): number {
  const nycGutter = 12; // Defined in less/nyc/variables.less
  // In expanded.less, a negative margin of 100%/20 is added to both sides, so the column
  // is now 1/22 of the width.
  return (tableWidth - 2 * nycGutter) / 22 + nycGutter;
}

/**
 * A scrollable table that can be used in "expanded" UIs.
 *
 * For proper sizing, this component must be nested within an `<nyc.Col lg={20}>` that is surrounded by
 * an empty `<nyc.Col lg={1} />` before and after.
 *
 * @example
 * <nyc.Container size="lg">
 *   <nyc.Row fluid={true}>
 *     <nyc.Col lg={1} />
 *     <nyc.Col lg={20}>
 *       <ExpandedScrollableTable ... />
 *     </nyc.Col>
 *     <nyc.Col lg={1} />
 *    </nyc.Row>
 * </nyc.Container>
 */
function ExpandedScrollableTable(props: ExpandedScrollableTableProps): JSX.Element {
  const childProps = omit(props, 'className');
  return (
    <div className={classNames('expandedScrollableTable u-flex-display u-vertical-flex', props.className)}>
      <ScrollableTable calcLeftPadding={calcExpandedLeftPadding} className="u-flex-grow" {...childProps} />
    </div>
  );
}

export default ExpandedScrollableTable;
